import gql from 'graphql-tag'
import {
  APPRAISAL_RECORD,
  APPRAISAL_TEMPLATE,
  APPRAISAL_ASSIGN,
  APPRAISAL_ASSIGN_WITH_ASSIGN_DETAILS,
  APPRAISAL_FIELD,
} from '../fragments/performance'
import { User } from '~/graphql/fragments/user.js'
import { V2_CHECKLIST_EVALUATION_DETAIL } from '~/graphql/fragments/performance.js'

export const GET_APPRAISAL_STANDARDS = gql`
  query appraisalStandards {
    appraisalStandards {
      name
      code
      description
      appraisalTemplates {
        ...AppraisalTemplate
      }
    }
  }
  ${APPRAISAL_TEMPLATE}
`

export const GET_APPRAISAL_TEMPLATE_DETAIL = gql`
  query ($appraisalTemplateId: Int!) {
    appraisalTemplate(appraisalTemplateId: $appraisalTemplateId) {
      ...AppraisalTemplate
    }
  }
  ${APPRAISAL_TEMPLATE}
`

export const GET_APPRAISAL_RECORDS = gql`
  query appraisalRecordsV2($year: Int, $limit: Int, $page: Int, $type: AppraisalType!, $schoolId: Int!) {
    appraisalRecordsV2(year: $year, limit: $limit, page: $page, type: $type, schoolId: $schoolId) {
      data {
        ...AppraisalRecord
      }
      meta {
        limit
        page
        total
      }
    }
  }
  ${APPRAISAL_RECORD}
`

export const GET_SELF_APPRAISALS = gql`
  query selfAppraisals($personalProfileId: Int!) {
    selfAppraisals(personalProfileId: $personalProfileId) {
      ...AppraisalRecord
    }
  }
  ${APPRAISAL_RECORD}
`

export const GET_OTHER_APPRAISALS = gql`
  query getOtherAppraisals($personalProfileId: Int!, $date: Date, $state: AppraisalRecordState) {
    othersAppraisals(personalProfileId: $personalProfileId, date: $date, state: $state) {
      ...AppraisalRecord
    }
  }
  ${APPRAISAL_RECORD}
`

export const GET_SCHOOL_APPRAISAL_RECORDS_ASSIGN = gql`
  query getAppraisalAssigns($schoolId: Int, $personalProfileId: Int, $reviewerPersonalProfileId: Int, $date: Date) {
    appraisalAssigns(
      schoolId: $schoolId
      personalProfileId: $personalProfileId
      reviewerPersonalProfileId: $reviewerPersonalProfileId
      date: $date
    ) {
      ...AppraisalAssign
    }
  }
  ${APPRAISAL_ASSIGN}
`

export const GET_APPRAISAL_RECORD_DETAIL = gql`
  query appraisalRecord($appraisalRecordId: Int!, $personalProfileId: Int!) {
    appraisalRecord(appraisalRecordId: $appraisalRecordId, personalProfileId: $personalProfileId) {
      ...AppraisalRecord
      appraisal {
        name
        description
        standard
        type
        appraisalFields {
          children {
            children {
              children {
                children {
                  description
                  name
                  parentId
                }
                description
                name
                parentId
              }
              description
              name
              parentId
            }
            description
            name
            parentId
          }
          description
          name
          parentId
        }
        appraisalFramework {
          name
          frequency
          appraisalSchedules {
            startDate
            endDate
          }
        }
      }
    }
  }
  ${APPRAISAL_RECORD}
`

export const GET_APPRAISAL_FRAMEWORKS = gql`
  query appraisalFrameworks($schoolId: Int!, $statuses: [AppraisalFrameworkStatus], $limit: Int, $page: Int) {
    appraisalFrameworks(schoolId: $schoolId, statuses: $statuses, limit: $limit, page: $page) {
      data {
        id
        name
        appraisals {
          id
          name
          standard
          type
          description
        }
        status {
          status
        }
        standardCode
        updatedAt
        frequency
        applyToFutureYear
        appraisalSchedules {
          startDate
          endDate
        }
      }
      meta {
        limit
        page
        total
      }
    }
  }
`

export const GET_ACTIVATED_FRAMEWORKS = gql`
  query appraisalFrameworks($schoolId: Int!, $statuses: [AppraisalFrameworkStatus]) {
    appraisalFrameworks(schoolId: $schoolId, statuses: $statuses) {
      data {
        id
        status {
          status
        }
        standardCode
      }
    }
  }
`

export const GET_APPRAISAL_FRAMEWORK_DETAIL = gql`
  query appraisalFrameworkDetail($id: Int!) {
    appraisalFramework(id: $id) {
      id
      name
      standardCode
      status {
        user {
          ...User
        }
        status
        updatedAt
      }
      prevStatus {
        user {
          ...User
        }
        status
        updatedAt
      }
      frequency
      updatedAt
      appraisals {
        id
        name
        type
        order
        appraisalFields {
          ...AppraisalField
          children {
            ...AppraisalField
            children {
              ...AppraisalField
              children {
                ...AppraisalField
                children {
                  ...AppraisalField
                }
              }
            }
          }
        }
      }
    }
  }
  ${User}
  ${APPRAISAL_FIELD}
`

export const GET_APPRAISAL_FRAMEWORKS_LOGS = gql`
  query appraisalFrameworkLogs($schoolId: Int!, $limit: Int, $page: Int) {
    appraisalFrameworkLogs(schoolId: $schoolId, limit: $limit, page: $page) {
      data {
        description
        status
        updatedAt
        appraisalFramework {
          name
        }
        user {
          ...User
        }
      }
      meta {
        limit
        page
        total
      }
    }
  }
  ${User}
`

export const GET_APPRAISAL_FRAMEWORKS_WITHOUT_FIELDS = gql`
  query appraisalFrameworks($schoolId: Int!, $statuses: [AppraisalFrameworkStatus], $limit: Int, $page: Int) {
    appraisalFrameworks(schoolId: $schoolId, statuses: $statuses, limit: $limit, page: $page) {
      data {
        id
        name
      }
      meta {
        limit
        page
        total
      }
    }
  }
`

export const GET_APPRAISAL_SUMMARY = gql`
  query getAppraisalSummary($schoolId: Int!, $date: Date) {
    appraisalSummary(schoolId: $schoolId, date: $date) {
      staffEnrolled
      appraisalRecords
    }
  }
`

export const GET_APPRAISAL_ASSIGN_DETAIL = gql`
  query getAppraisalAssignDetails($id: Int!) {
    appraisalAssign(id: $id) {
      ...AppraisalAssignWithAssignDetails
    }
  }
  ${APPRAISAL_ASSIGN_WITH_ASSIGN_DETAILS}
`

export const GET_APPRAISAL_PERIODS = gql`
  query appraisalSchedules($schoolId: Int!) {
    appraisalSchedules(schoolId: $schoolId) {
      id
      name
      startDate
      endDate
    }
  }
`

export const GET_APPRAISAL_ASSIGN_AUDITS = gql`
  query appraisalAssignAudits($schoolId: Int!, $limit: Int, $page: Int) {
    appraisalAssignAudits(schoolId: $schoolId, limit: $limit, page: $page) {
      meta {
        limit
        page
        total
      }
      data {
        actorRefId
        effectedRefId
        action
        insertedAt
      }
    }
  }
`

export const V2_PERFORMANCE_LIST_CHECKLIST_EVALUATIONS = gql`
  query v2PerformanceGetChecklistEvaluations(
    $limit: Int
    $mode: ListChecklistEvaluationMode!
    $page: Int
    $schoolIds: [Int]
    $statuses: [String]
    $sortField: V2PerformanceGetChecklistEvaluationsAllowedSortField
    $sortDirection: SortDirection
    $checklistRoles: [String]
    $designations: [String]
    $organisationTitleIds: [String]
    $search: String
  ) {
    v2PerformanceGetChecklistEvaluations(
      limit: $limit
      mode: $mode
      page: $page
      schoolIds: $schoolIds
      statuses: $statuses
      sortField: $sortField
      sortDirection: $sortDirection
      checklistRoles: $checklistRoles
      designations: $designations
      organisationTitleIds: $organisationTitleIds
      search: $search
    ) {
      checklistEvaluationStatuses
      checklistRoles
      data {
        id
        status
        role
        overdue
        checklist {
          id
        }
        signOffComments: performanceChecklistEvaluationComments(types: [SIGN_OFF_COMMENT]) {
          id
          author {
            id
            name
            media {
              url
            }
          }
          comment
          updatedAt
        }
        roleGroup
        period {
          name
          startDate
          endDate
        }
        appraisee {
          name
          designation
          school {
            name
          }
          media {
            url
          }
        }
        participants {
          roleId
          name
          type
          userId
          roleName
        }
        checklistRecords {
          id
          roleId
          value
          checklistId
          normalizedValue
        }
        buttons {
          buttonText
          buttonType
          actionType
          actionParams
        }
        updatedAt
      }
      meta {
        limit
        page
        total
      }
    }
  }
`

export const V2_PERFORMANCE_LIST_FRAMEWORKS = gql`
  query v2PerformanceListFrameworks($limit: Int, $offset: Int, $schoolIds: [Int!]!) {
    v2PerformanceListFrameworks(limit: $limit, offset: $offset, schoolIds: $schoolIds) {
      data {
        id
        name
        status
        stateSettings
      }
    }
  }
`

export const V2_PERFORMANCE_LIST_CHECKLIST_EVALUATION_DETAIL = gql`
  query v2PerformanceGetChecklistEvaluationDetail($id: Int!) {
    v2PerformanceGetChecklistEvaluationDetail(id: $id) {
      ...V2PerformanceChecklistEvaluationDetail
    }
  }
  ${V2_CHECKLIST_EVALUATION_DETAIL}
`

export const V2_ADMIN_PERFORMANCE_CHECKLIST_EVALUATION = gql`
  query v2AdminPerformanceGetChecklistEvaluationDetail($id: Int!, $userId: Int!) {
    v2AdminPerformanceGetChecklistEvaluationDetail(id: $id, userId: $userId) {
      ...V2PerformanceChecklistEvaluationDetail
    }
  }
  ${V2_CHECKLIST_EVALUATION_DETAIL}
`

export const V2_ADMIN_PERFORMANCE_CHECKLIST_EVALUATIONS = gql`
  query v2AdminPerformanceGetChecklistEvaluations(
    $limit: Int
    $mode: ListChecklistEvaluationMode!
    $page: Int
    $schoolIds: [Int]
    $userId: Int!
  ) {
    v2AdminPerformanceGetChecklistEvaluations(
      limit: $limit
      mode: $mode
      page: $page
      schoolIds: $schoolIds
      userId: $userId
    ) {
      data {
        id
        status
        role
        roleGroup
        period {
          name
          startDate
          endDate
        }
        appraisee {
          name
          designation
          school {
            name
          }
          media {
            url
          }
        }
        participants {
          roleId
          name
          type
        }
        checklistRecords {
          id
          roleId
          value
          checklistId
          normalizedValue
        }
        buttons {
          buttonText
          buttonType
          actionType
          actionParams
        }
        updatedAt
      }
      meta {
        limit
        page
        total
      }
    }
  }
`

export const V2_PERFORMANCE_GET_FRAMEWORK_DETAIL = gql`
  query v2PerformanceGetFrameworkDetail($id: Int!) {
    v2PerformanceGetFrameworkDetail(id: $id) {
      name
      status
      stateSettings
      performanceAppraiserRoles {
        id
        name
        type
      }
      performancePeriods {
        id
        name
        startDate
        endDate
      }
      performanceChecklists {
        id
        roleName
        roleGroup
        calculationSettings
        performanceChecklistItems {
          id
          calculationSettings
          name
          description
          parentId
        }
      }
    }
  }
`

export const V2_PERFORMANCE_EXPORT_CHECKLIST = gql`
  query v2PerformanceExportChecklist($frameworkId: Int!, $periodId: Int) {
    v2PerformanceExportChecklist(frameworkId: $frameworkId, periodId: $periodId, fixed: true) {
      url
      message
    }
  }
`
